/**
 * How hard a weapon hits *for this build* — the rankings behind the search
 * panel's weapon sort.
 *
 * Two questions, because they have different answers. **Per turn** is "what does
 * my turn look like holding this", so it counts the casts the AP bar pays for: a
 * 3 AP dagger swung four times beats a 12 AP hammer that lands once. **Per AP**
 * is "what does each point buy", which is the question you ask when the weapon
 * is one line in a turn that also casts spells — there the hammer's single big
 * hit can win. Both start from the same average hit (crit-weighted, elements
 * resolved against the build's stats — everything `calcWeaponCardRow` already
 * computes for the spell panel); they differ only in what they divide by.
 *
 * Every candidate is priced on the sheet it would actually be swung on: the
 * build with its current weapon *removed* and the candidate equipped in its
 * place. Scoring them all on the build's sheet as-is — which this did until it
 * was measured — is wrong twice over. The equipped weapon's Power, damage and
 * critical are lent to every rival, and no rival is credited with its own, so a
 * weapon carrying stats is ranked as if it were bare dice and the number on the
 * card answers a question nobody asked: what this weapon does while you also
 * keep the one you are replacing.
 *
 * The swap is priced as a delta rather than by rebuilding the Build, because a
 * rebuild per candidate is the cost that made the shortcut look reasonable:
 * over 719 weapons it is ~133 ms against ~9 ms for the delta, and the delta is
 * ~2 ms more than pricing them all on one sheet. See `createWeaponSheetFactory`
 * for what the delta covers and what it deliberately does not.
 */

import * as st from './st';
import { SLOT_IDX, itemEffectsMap } from './Build';
import { MIN_ACTIVE_PIECES, tiersFor } from './setBonusesFromCatalog';
import { peekItemCatalog } from '../services/itemCatalog';
import { calcWeaponHitLines, statsAtPosition } from './spellUtils';

// A weapon with no AP cost on record is scored as a single cast rather than as
// free — unknown must not read as "infinitely spammable".
const FALLBACK_CASTS = 1;
// `max_cast` is null or 0 for weapons with no per-turn limit of their own.
const NO_CAST_LIMIT = Infinity;

/** The orderings the weapon section offers. `LEVEL` is the catalog's own field. */
export const WEAPON_SORT_LEVEL = 'level';
export const WEAPON_SORT_DAMAGE = 'damage';
export const WEAPON_SORT_DAMAGE_PER_AP = 'damage_per_ap';

const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Casts a turn buys: what the build's AP affords, capped by the weapon's own limit. */
function castsPerTurn(weapon, apCost, turnAp) {
    const maxCast = toNum(weapon?.max_cast);
    const limit = maxCast > 0 ? maxCast : NO_CAST_LIMIT;
    if (!(apCost > 0)) return Math.min(limit, FALLBACK_CASTS);
    const affordable = Math.floor(turnAp / apCost);
    return Math.min(limit, affordable);
}

/**
 * Expected damage per turn for `item` on a build with `stats`.
 * Returns 0 for anything that is not a damaging weapon, so non-weapons sort last.
 *
 * A heal counts against the hit, the same way the find-the-best-weapon criterion
 * counts it (`spell_scoring._line_from_weapon_effect`, `subtract`): a wand that
 * heals its target 1-100 Fire is not doing that damage, it is undoing it. A
 * weapon that nets healing falls to 0 here and sorts with the non-weapons,
 * which is where "most damage" should put it.
 */
export function weaponDamageOutput(item, { stats, lvl = null } = {}) {
    if (!item?.weapon || !stats) return 0;
    const hit = calcWeaponHitLines({ weapon: item.weapon, stats, lvl });
    const perHit = toNum(hit?.damageTotal) - toNum(hit?.healTotal);
    if (perHit <= 0) return 0;
    const turnAp = toNum(stats[st.AP_ID]?.current);
    return perHit * castsPerTurn(item.weapon, toNum(item.weapon.ap), turnAp);
}

/**
 * Expected damage per AP spent on `item` — the same average hit, priced by what
 * it costs instead of by what a turn affords.
 *
 * The cast count cancels out of the division, so a weapon's own `max_cast` limit
 * does not change its efficiency: two casts of a 3 AP dagger buy exactly what one
 * buys, per point. What the limit still does is decide whether the weapon is
 * usable at all — an unaffordable one scores 0 here as it does per turn, so the
 * two orderings agree on which weapons belong at the bottom.
 */
export function weaponDamagePerAp(item, { stats, lvl = null } = {}) {
    const apCost = toNum(item?.weapon?.ap);
    // No cost on record cannot read as free: dividing by nothing would rank the
    // weapon first, the mirror of the FALLBACK_CASTS rule above.
    if (apCost <= 0) return 0;
    const perTurn = weaponDamageOutput(item, { stats, lvl });
    if (perTurn <= 0) return 0;
    const casts = castsPerTurn(item.weapon, apCost, toNum(stats[st.AP_ID]?.current));
    return perTurn / (casts * apCost);
}

const SORT_SCORES = {
    [WEAPON_SORT_DAMAGE]: weaponDamageOutput,
    [WEAPON_SORT_DAMAGE_PER_AP]: weaponDamagePerAp,
};

/**
 * What a candidate weapon's set would add on top of the build it is joining.
 *
 * A weapon is a set piece like any other: equipping the fifth Ourse piece is
 * worth its tier bonus, and the tier the *current* weapon was holding up is
 * lost. Both sides are read from the piece counts of the weaponless build, so
 * the delta is `tier(held + 1) - tier(held)` — nothing when the candidate is in
 * no set, which is the common case (552 of 719 weapons).
 *
 * The tier is indexed the way `_calculateStats` indexes it, `bonuses[pieces-2]`,
 * so a build that gets this bonus through the delta and one that gets it by
 * being rebuilt agree. `tiersFor`'s list does not depend on the count it is
 * passed — only its `is_active` flags do, and those are not read here.
 */
function createSetBonusDelta(items) {
    const catalog = peekItemCatalog();
    if (!catalog) return () => null;

    const held = new Map();
    for (const item of items || []) {
        const setId = item?.item_set_id;
        if (setId === null || setId === undefined) continue;
        held.set(setId, (held.get(setId) || 0) + 1);
    }

    const tiers = new Map();
    const effectsAt = (setId, pieces) => {
        if (pieces < MIN_ACTIVE_PIECES) return null;
        if (!tiers.has(setId)) tiers.set(setId, tiersFor(catalog.setById.get(setId), pieces));
        return tiers.get(setId)[pieces - MIN_ACTIVE_PIECES]?.effects || null;
    };

    return (item) => {
        const setId = item?.item_set_id;
        if (setId === null || setId === undefined) return null;
        const pieces = held.get(setId) || 0;
        const gained = effectsAt(setId, pieces + 1);
        const given = effectsAt(setId, pieces);
        if (!gained && !given) return null;
        const delta = {};
        for (const effect of gained || []) {
            delta[effect.characteristic] = (delta[effect.characteristic] || 0) + effect.value;
        }
        for (const effect of given || []) {
            delta[effect.characteristic] = (delta[effect.characteristic] || 0) - effect.value;
        }
        return delta;
    };
}

/**
 * A function from candidate weapon to the sheet it would be swung on.
 *
 * The base is computed once: the build with the weapon slot emptied, set
 * bonuses re-derived by `removeItem` like any other unequip. Each candidate is
 * then that base plus its own stat lines and its own set tier — one shallow copy
 * of the sheet, and one new entry per line the weapon carries.
 *
 * What the delta does **not** redo is the derived block at the end of
 * `_calculateStats`: initiative, prospecting, pods, lock/dodge and the wisdom
 * reductions still read the base build's characteristics. None of them reach
 * weapon damage, and this sheet exists only to be handed to
 * `calcWeaponHitLines` — so it is not a general-purpose stat sheet and must not
 * be shown as one.
 */
export function createWeaponSheetFactory(build) {
    const bare = build?.removeItem ? build.removeItem(SLOT_IDX.WEAPON) : null;
    const base = bare?.getStats?.() || null;
    if (!base) return () => null;
    const setBonusDelta = createSetBonusDelta(bare.items);

    return (item) => {
        const delta = { ...(itemEffectsMap(item) || {}) };
        const fromSet = setBonusDelta(item);
        if (fromSet) {
            for (const id in fromSet) delta[id] = (delta[id] || 0) + fromSet[id];
        }
        const sheet = { ...base };
        for (const id in delta) {
            const stat = base[id];
            if (!stat || !delta[id]) continue;
            sheet[id] = { ...stat, current: stat.current + delta[id] };
        }
        return sheet;
    };
}

/**
 * The factories, one per build. Search scores the whole weapon list and the
 * cards each price their own item, and all of them want the same weaponless
 * base — cached on the build itself so it is derived once rather than once per
 * caller. A build is replaced on every edit, so the entry dies with it.
 */
const SHEET_FACTORIES = new WeakMap();

/**
 * The sheet `item` would be swung on if it were equipped on `build`, fighting
 * at `position` (genConfig.position).
 */
export function weaponSheetFor(build, item, position) {
    if (!build) return null;
    let factory = SHEET_FACTORIES.get(build);
    if (!factory) {
        factory = createWeaponSheetFactory(build);
        SHEET_FACTORIES.set(build, factory);
    }
    return statsAtPosition(factory(item), position);
}

/**
 * A scorer for one build and one ordering, memoized by item id. Returns null for
 * an ordering the catalog can answer by itself (level), which is the signal the
 * search takes to sort on the item's own field.
 *
 * Sorting is over every candidate, not over the page, so a search scores the
 * whole weapon list — and a keystroke scores it again. The cache makes that one
 * pass per build instead of one per keystroke.
 */
export function createWeaponDamageScorer({ build, sort = WEAPON_SORT_DAMAGE, position } = {}) {
    const score = SORT_SCORES[sort];
    if (!score || !build) return null;
    const lvl = build.getLvl?.() ?? build.lvl ?? null;
    const cache = new Map();
    const priced = (item) => score(item, { stats: weaponSheetFor(build, item, position), lvl });
    return (item) => {
        const id = item?.id;
        if (id == null) return priced(item);
        if (cache.has(id)) return cache.get(id);
        const value = priced(item);
        cache.set(id, value);
        return value;
    };
}
